import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { InventoryItem, KpiSummary, ColumnDef, SortLevel } from './types/inventory';
import { OABLineItem, OrderReference } from './types/oab';
import { loadInventoryData, fetchBCVRate, revalidateInventoryLive, forceRevalidateNotion, saveStockOverride } from './services/inventoryService';
import { Header } from './components/Header';
import { KpiCards } from './components/KpiCards';
import { FilterBar } from './components/FilterBar';
import { InventoryTable } from './components/InventoryTable';
import { GlossaryModal } from './components/GlossaryModal';
import { SupplyOrderModal } from './components/SupplyOrderModal';
import { ReceptionTerminalModal } from './components/ReceptionTerminalModal';
import { OABReviewModal } from './components/OABReviewModal';
import { ErrorBoundary } from './components/ErrorBoundary';
import { KardexViewerModal } from './components/KardexViewerModal';
import { PinLoginModal } from './components/PinLoginModal';
import { AccessDeniedModal } from './components/AccessDeniedModal';
import { ChangePinModal } from './components/ChangePinModal';
import { AccessAuditModal } from './components/AccessAuditModal';
import { MaterialDispatchModal } from './components/MaterialDispatchModal';
import { OrderBOMAuditModal } from './components/OrderBOMAuditModal';
import { StockAdjustmentModal } from './components/StockAdjustmentModal';
import { OfflineQueueModal } from './components/OfflineQueueModal';
import { PrintCountingSheetModal } from './components/PrintCountingSheetModal';
import { BarcodeCollectorModal } from './components/BarcodeCollectorModal';
import { BultoVerificationModal, BultoData } from './components/BultoVerificationModal';
import { PrintSheetProjectLabels } from './components/PrintSheetProjectLabels';
import { StoreAllocationModal } from './components/StoreAllocationModal';
import { CommercialProjectsHubModal } from './components/CommercialProjectsHubModal';
import { ItemMasterModal } from './components/ItemMasterModal';
import { CatalogItem } from './types/catalog';
import { initScannerQueue, subscribeQueue, subscribeItemProcessed } from './services/scannerQueueService';
import { getPendingAdjustmentsCount } from './services/offlineStorageService';
import { syncPendingAdjustments } from './services/offlineSyncService';
import { fetchLiveAllocations, AllocationsResponse, StoreAllocation, getInsumoAllocationSummary } from './services/inventoryService';
import { StockAdjustmentResult } from './types/adjustment';
import { ReverseKardexResult } from './services/kardexService';
import { Trash2 } from 'lucide-react';
import { useTelegramAuth } from './hooks/useTelegramAuth';
import { PermissionKey } from './types/auth';

export const allColumnsDef: ColumnDef[] = [
  { key: 'nombre', label: 'Nombre', sortable: true, groupable: false, align: 'left' },
  { key: 'codigo', label: 'Código', sortable: true, groupable: false, align: 'left' },
  { key: 'marca', label: 'Marca', sortable: true, groupable: true, align: 'left' },
  { key: 'stockBase', label: 'Stock', sortable: true, groupable: false, align: 'right' },
  { key: 'stockApartado', label: 'Apartado (Tiendas)', sortable: true, groupable: false, align: 'right' },
  { key: 'stockMinimo', label: 'Stock Mín.', sortable: true, groupable: false, align: 'right' },
  { key: 'deficit', label: 'Déficit', sortable: true, groupable: false, align: 'right' },
  { key: 'enTransitoOAB', label: 'En Tránsito (OAB)', sortable: true, groupable: false, align: 'right' },
  { key: 'stockProyectado', label: 'Stock Proy.', sortable: true, groupable: false, align: 'right' },
  { key: 'costoUnitarioUSD', label: 'Costo Unit. ($)', sortable: true, groupable: false, align: 'right' },
  { key: 'estadoStock', label: 'Estado', sortable: true, groupable: true, align: 'left' },
  { key: 'prioridad', label: 'Prioridad', sortable: true, groupable: true, align: 'left' },
  { key: 'categoriaMaterial', label: 'Categoría', sortable: true, groupable: true, align: 'left' },
  { key: 'rolMaterial', label: 'Rol Material', sortable: true, groupable: true, align: 'left' },
  { key: 'origenConsumo', label: 'Origen Consumo', sortable: true, groupable: true, align: 'left' },
  { key: 'unidad', label: 'Unidad', sortable: true, groupable: true, align: 'left' },
  { key: 'color', label: 'Color', sortable: true, groupable: false, align: 'left' },
  { key: 'dimensiones', label: 'Dimensiones', sortable: false, groupable: false, align: 'left' },
  { key: 'grupoProceso', label: 'Grupo Proceso', sortable: true, groupable: true, align: 'left' },
  { key: 'proceso', label: 'Proceso', sortable: true, groupable: true, align: 'left' },
  { key: 'departamento', label: 'Departamento', sortable: true, groupable: true, align: 'left' },
  { key: 'seReconto3D', label: 'Reconteo 3D', sortable: true, groupable: false, align: 'left' },
  { key: 'diasDesdeReconteo', label: 'Días Reconteo', sortable: true, groupable: false, align: 'right' },
];

export const compactColumnKeys = [
  'nombre',
  'stockBase',
  'stockApartado',
  'stockMinimo',
  'deficit',
  'enTransitoOAB',
  'stockProyectado',
  'estadoStock',
  'prioridad'
];

export default function App() {
  // Data State
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [baseKpis, setBaseKpis] = useState<KpiSummary>({
    total: 0,
    estado: { sinStock: 0, bajoMinimo: 0, enStock: 0, enReconteo: 0, descontinuado: 0 },
    prioridad: { urgente: 0, alta: 0, media: 0, baja: 0, porPedido: 0 },
    auditados3D: 0,
    auditados3DPct: 0,
  });
  const [selectOrders, setSelectOrders] = useState<Record<string, string[]>>({});
  const [lastSyncDisplay, setLastSyncDisplay] = useState('—');
  const [bcvRate, setBcvRate] = useState(880.00);
  const [bcvStatus, setBcvStatus] = useState<'success' | 'stale_cache' | 'fallback'>('success');
  const [bcvUpdatedAt, setBcvUpdatedAt] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Filter & View State
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'compact' | 'expanded' | 'custom'>('compact');
  const [visibleColumns, setVisibleColumns] = useState<string[]>(compactColumnKeys);
  const [sortLevels, setSortLevels] = useState<SortLevel[]>([
    { key: 'prioridad', dir: 'asc' },
    { key: 'deficit', dir: 'desc' }
  ]);
  const [groupByKey, setGroupByKey] = useState<string | null>(null);
  const [quickFilters, setQuickFilters] = useState<{ estadoStock: string[]; prioridad: string[] }>({
    estadoStock: [],
    prioridad: []
  });
  const [onlyDeficit, setOnlyDeficit] = useState(false);
  const [onlyPendingRecount, setOnlyPendingRecount] = useState(false);

  // Auth & RBAC State
  const auth = useTelegramAuth();
  const [deniedModalOpen, setDeniedModalOpen] = useState(false);
  const [deniedTargetModule, setDeniedTargetModule] = useState('Módulo Protegido');

  // Modals
  const [supplyModalOpen, setSupplyModalOpen] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [receptionModalOpen, setReceptionModalOpen] = useState(false);
  const [receptionInitialSearch, setReceptionInitialSearch] = useState<string | undefined>(undefined);
  const [kardexModalOpen, setKardexModalOpen] = useState(false);
  const [kardexTargetMaterial, setKardexTargetMaterial] = useState<{ id: string; nombre: string; stock: number } | null>(null);
  const [draftItemsForModal, setDraftItemsForModal] = useState<InventoryItem[]>([]);
  const [draftBasket, setDraftBasket] = useState<InventoryItem[]>([]);
  const [supplyInitialLines, setSupplyInitialLines] = useState<OABLineItem[] | undefined>(undefined);
  const [supplyInitialNotes, setSupplyInitialNotes] = useState<string | undefined>(undefined);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [deepLinkFolio, setDeepLinkFolio] = useState<string | null>(null);
  const [changePinModalOpen, setChangePinModalOpen] = useState(false);
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [dispatchModalOpen, setDispatchModalOpen] = useState(false);
  const [dispatchPreselectedItem, setDispatchPreselectedItem] = useState<InventoryItem | null>(null);
  const [dispatchPreselectedOrder, setDispatchPreselectedOrder] = useState<OrderReference | null>(null);
  const [dispatchPreselectedQty, setDispatchPreselectedQty] = useState<number | null>(null);
  const [dispatchPreselectedNivel, setDispatchPreselectedNivel] = useState<'GENERAL' | 'TIENDA' | 'MOBILIARIO'>('GENERAL');
  const [bomAuditModalOpen, setBomAuditModalOpen] = useState(false);
  const [bomAuditPreselectedOrder, setBomAuditPreselectedOrder] = useState<{ id?: string; codigo?: string; nombre?: string } | null>(null);
  const [adjustmentModalOpen, setAdjustmentModalOpen] = useState(false);
  const [adjustmentPreselectedItem, setAdjustmentPreselectedItem] = useState<InventoryItem | null>(null);
  const [offlineQueueCount, setOfflineQueueCount] = useState(0);
  const [offlineQueueModalOpen, setOfflineQueueModalOpen] = useState(false);
  const [printSheetModalOpen, setPrintSheetModalOpen] = useState(false);
  const [barcodeCollectorOpen, setBarcodeCollectorOpen] = useState(false);
  const [scannerQueueCount, setScannerQueueCount] = useState(0);
  const [bultoModalOpen, setBultoModalOpen] = useState(false);
  const [bultoModalData, setBultoModalData] = useState<BultoData | null>(null);
  const [reprintLabelsItems, setReprintLabelsItems] = useState<any[] | null>(null);

  // Estado de Asignaciones y Stock Comprometido por Tienda (Fase 10C)
  const [allocationsData, setAllocationsData] = useState<AllocationsResponse | null>(null);
  const [storeAllocationModalOpen, setStoreAllocationModalOpen] = useState(false);
  const [storeAllocInitialSearch, setStoreAllocInitialSearch] = useState<string | null>(null);
  const [commercialHubModalOpen, setCommercialHubModalOpen] = useState(false);
  const [selectedItemForAlloc, setSelectedItemForAlloc] = useState<InventoryItem | null>(null);
  const [selectedAllocForReassign, setSelectedAllocForReassign] = useState<StoreAllocation | null>(null);

  // Estado del Catálogo Maestro de Insumos & Health Checker (Fase 11)
  const [catalogModalOpen, setCatalogModalOpen] = useState(false);
  const [catalogModalInitialTab, setCatalogModalInitialTab] = useState<'directory' | 'form' | 'health'>('directory');
  const [catalogModalInitialItem, setCatalogModalInitialItem] = useState<CatalogItem | null>(null);

  const showToast = useCallback((msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(curr => (curr === msg ? null : curr));
    }, 3500);
  }, []);

  const loadAllocations = useCallback(async () => {
    try {
      const data = await fetchLiveAllocations();
      setAllocationsData(data);
      if (data?.summaryByDashboardId) {
        setItems(prevItems => prevItems.map(it => {
          const sum = getInsumoAllocationSummary(data, it);
          const ap = sum?.totalApartado || 0;
          return {
            ...it,
            stockApartado: ap,
            stockLibre: Math.max(0, (it.stockBase || 0) - ap)
          };
        }));
      }
    } catch (e) {
      console.warn('Error cargando live_stock_allocations:', e);
    }
  }, []);

  // Inicialización y reactividad de Cola de Escaneo en Ráfaga (Fase 9J)
  useEffect(() => {
    initScannerQueue();

    const unsubQueue = subscribeQueue((queue) => {
      const activeOrPending = queue.filter(q => q.status !== 'COMPLETADO');
      setScannerQueueCount(activeOrPending.length);
    });

    const unsubProcessed = subscribeItemProcessed((result, queueItem) => {
      if (result.newStock !== undefined && result.status === 'success') {
        saveStockOverride(result.dashboardId || queueItem.dashboardId, result.newStock);
        setItems(currItems => {
          return currItems.map(item => {
            if (item.id === (result.dashboardId || queueItem.dashboardId)) {
              const newBaseStock = result.newStock!;
              const def = Math.max(0, (item.stockMinimo || 0) - newBaseStock);
              const est = newBaseStock === 0 ? 'Sin Stock' : (newBaseStock < item.stockMinimo ? 'Bajo Mínimo' : 'En Stock');
              return {
                ...item,
                stockBase: newBaseStock,
                deficit: def,
                estadoStock: est,
                stockProyectado: newBaseStock + (item.enTransitoOAB || 0),
                seReconto3D: true,
                diasDesdeReconteo: 0
              };
            }
            return item;
          });
        });
        showToast(`⚡ Ajuste de ${queueItem.nombre} asentado en Notion (${result.folio || 'Kardex'})`);
      }
    });

    return () => {
      unsubQueue();
      unsubProcessed();
    };
  }, [showToast]);

  // Atajo global de teclado para Modo Pistola (F2 o Alt+B)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2' || (e.altKey && (e.key === 'b' || e.key === 'B'))) {
        e.preventDefault();
        if (auth.hasPermission('Ajustes' as PermissionKey) || auth.profile?.permissions?.includes('Superadmin')) {
          setBarcodeCollectorOpen(prev => !prev);
        } else {
          setDeniedTargetModule('Modo Pistola de Código de Barras');
          setDeniedModalOpen(true);
          auth.triggerHaptic('error');
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [auth]);

  // Monitoreo de Cola Offline y Sincronización Automática al Detectar Red (Fase 9G.2)
  const refreshOfflineCount = useCallback(async () => {
    try {
      const count = await getPendingAdjustmentsCount();
      setOfflineQueueCount(count);
    } catch (e) {
      console.warn('Error leyendo conteos offline:', e);
    }
  }, []);

  useEffect(() => {
    refreshOfflineCount();

    const handleOnline = async () => {
      auth.triggerHaptic('success');
      const count = await getPendingAdjustmentsCount();
      if (count > 0) {
        showToast('📡 Conexión restablecida: Sincronizando conteos locales con Notion ERP...');
        try {
          const res = await syncPendingAdjustments(bcvRate);
          if (res.syncedCount > 0) {
            showToast(`✅ Sincronizados ${res.syncedCount} conteos offline con éxito.`);
            refreshOfflineCount();
            setTimeout(() => {
              setItems(current => {
                revalidateInventoryLive(current).then(r => {
                  if (r.synced) setItems(r.updatedItems);
                });
                return current;
              });
            }, 2500);
          }
        } catch (err) {
          console.error('Error en auto-sync al reconectar:', err);
        }
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [bcvRate, refreshOfflineCount, showToast, auth]);

  // Auto-apertura por Deep-Links sincronizada con perfil, login diferido y permisos (D1-10B.1 / PLAN-045)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const bultoParam = params.get('bulto');
      const materialParam = params.get('material');
      const tiendaParam = params.get('tienda');
      const folioParam = params.get('folio') || params.get('oabReview');
      const targetParam = params.get('target');
      const rampaParam = params.get('rampa');
      const rampReceiptParam = params.get('rampReceipt');
      const kardexParam = params.get('kardex');
      const cantBultoParam = params.get('cant_bulto');
      const pkgIdParam = params.get('pkg_id');

      // D1-10B.1: Intercepción Prioritaria para Cédula Digital del Bulto Móvil (Lectura Libre en Planta)
      if (bultoParam && materialParam) {
        setBultoModalData({
          folio: folioParam || '',
          material: materialParam,
          tienda: tiendaParam || '',
          bulto: bultoParam,
          cantBulto: cantBultoParam ? parseFloat(cantBultoParam) : undefined,
          pkgId: pkgIdParam || undefined
        });
        setBultoModalOpen(true);
        return;
      }

      if (auth.loading) return;

      const hasProtectedDeepLink = Boolean(folioParam || rampReceiptParam || kardexParam === 'true');
      if (!hasProtectedDeepLink) return;

      // PLAN-045 (F2): Si el operador abre el enlace desde WhatsApp o Telegram en navegador sin sesión PIN activa,
      // diferir la apertura hasta que ingrese su PIN en vez de lanzar falso Acceso Denegado.
      if (!auth.profile) {
        showToast(`🔐 Ingrese su PIN de operador para abrir ${rampReceiptParam || folioParam || 'el módulo solicitado'}`);
        return;
      }

      const wantsRamp = targetParam === 'ramp' || rampaParam === 'true' || Boolean(rampReceiptParam);

      if (folioParam || rampReceiptParam) {
        if (folioParam) setDeepLinkFolio(folioParam);
        if (kardexParam === 'true') {
          if (auth.hasPermission('Auditoria_Kardex')) {
            setKardexModalOpen(true);
          } else {
            setDeniedTargetModule('Libro Mayor de Almacén (Kardex)');
            setDeniedModalOpen(true);
            auth.triggerHaptic('error');
          }
        } else if (wantsRamp) {
          if (auth.hasPermission('Recepcion_Rampa') || auth.hasPermission('Revisar_OAB') || auth.canAprobarOAB) {
            setReceptionInitialSearch(rampReceiptParam || folioParam || undefined);
            setReceptionModalOpen(true);
          } else {
            setDeniedTargetModule('Terminal de Recepción en Rampa');
            setDeniedModalOpen(true);
            auth.triggerHaptic('error');
          }
        } else {
          // Enrutamiento Inteligente por Rol acordado en Grill-Me:
          if (auth.canCotizarOAB || auth.canAprobarOAB || auth.canDelegarOAB || auth.hasPermission('Revisar_OAB')) {
            setReviewModalOpen(true);
          } else if (auth.hasPermission('Recepcion_Rampa')) {
            setReceptionInitialSearch(folioParam || undefined);
            setReceptionModalOpen(true);
          } else {
            setDeniedTargetModule('Gestión Digital OAB (Revisión / Rampa)');
            setDeniedModalOpen(true);
            auth.triggerHaptic('error');
          }
        }
      } else if (kardexParam === 'true') {
        if (auth.hasPermission('Auditoria_Kardex')) {
          setKardexModalOpen(true);
        } else {
          setDeniedTargetModule('Libro Mayor de Almacén (Kardex)');
          setDeniedModalOpen(true);
          auth.triggerHaptic('error');
        }
      }
    } catch (e) {
      console.warn('Error interpretando deep-links:', e);
    }
  }, [auth.loading, auth.profile, showToast]);

  // Load Initial Data
  const fetchData = useCallback(async () => {
    try {
      setLoadError(false);
      const data = await loadInventoryData();
      setItems(data.items);
      setBaseKpis(data.kpis);
      setSelectOrders(data.selectOrders);
      setLastSyncDisplay(data.lastSyncDisplay);

      const bcv = await fetchBCVRate();
      setBcvRate(bcv.rate);
      setBcvStatus(bcv.status);
      setBcvUpdatedAt(bcv.updatedAt);

      // Cargar mapa de reservas y asignaciones MTO desde Edge KV (Fase 10C)
      loadAllocations();

      // SWR: Revalidación en segundo plano contra Notion API vía Cloudflare Functions
      revalidateInventoryLive(data.items).then(result => {
        if (result.synced) {
          setItems(result.updatedItems);
          const now = new Date();
          const formatted = now.toLocaleDateString('es-VE', { 
            day: 'numeric', month: 'numeric', year: 'numeric', 
            hour: '2-digit', minute: '2-digit' 
          });
          setLastSyncDisplay(formatted);
        }
      }).catch(swrErr => console.warn('Background SWR:', swrErr));

    } catch (err) {
      console.error('Error fetching inventory:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();

    let lastSyncTime = Date.now();

    // SWR Reactivo Multi-Dispositivo: Revalidar bajo interacción real (al enfocar ventana o desbloquear pantalla)
    const handleVisibilityChange = () => {
      const now = Date.now();
      // Debounce de seguridad: al menos 15 segundos entre revalidaciones automáticas
      if (document.visibilityState === 'visible' && now - lastSyncTime > 15000) {
        lastSyncTime = now;
        setItems(current => {
          revalidateInventoryLive(current).then(res => {
            if (res.synced) {
              setItems(res.updatedItems);
              const nowTime = new Date();
              const formattedTime = nowTime.toLocaleDateString('es-VE', { 
                day: 'numeric', month: 'numeric', year: 'numeric', 
                hour: '2-digit', minute: '2-digit' 
              });
              setLastSyncDisplay(formattedTime);
            }
          });
          return current;
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [fetchData]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchData();
  };

  const [isRevalidatingNotion, setIsRevalidatingNotion] = useState(false);

  const handleRevalidateNotion = useCallback(async () => {
    setIsRevalidatingNotion(true);
    auth.triggerHaptic('light');
    showToast('🔄 Purgando caché Edge KV y consultando Notion en tiempo real...');
    try {
      await forceRevalidateNotion();
      await fetchData();
      auth.triggerHaptic('success');
      showToast('⚡ Inventario y Catálogo revalidados con éxito desde Notion.');
    } catch (e: any) {
      console.error('Error revalidando Notion:', e);
      auth.triggerHaptic('error');
      showToast('❌ Error revalidando con Notion.');
    } finally {
      setIsRevalidatingNotion(false);
    }
  }, [auth, fetchData, showToast]);

  // Handler cuando se emite una OAB:
  // NOTA ARQUITECTÓNICA (Odoo-Sanesca): La requisición 'Solicitado' NO suma a tránsito hasta ser aprobada y comprada.
  const handleOrderCreated = useCallback((createdLines: OABLineItem[]) => {
    // Vaciar la bandeja acumulativa de requisición
    setDraftBasket([]);

    setItems(prevItems => {
      return prevItems.map(item => {
        const line = createdLines.find(
          l => (l.dashboardId && l.dashboardId === item.id) ||
               (l.insumoId && l.insumoId === item.insumoId) ||
               (l.nombre && item.nombre && l.nombre.toLowerCase().trim() === item.nombre.toLowerCase().trim())
        );
        if (!line) return item;
        return {
          ...item,
          isOptimisticSync: true,
          syncNote: `Requisición registrada (Tránsito 0 hasta compra)`
        };
      });
    });

    // Reconciliar con Notion en segundo plano tras 2 segundos
    setTimeout(() => {
      setItems(current => {
        revalidateInventoryLive(current).then(res => {
          if (res.synced) setItems(res.updatedItems);
        });
        return current;
      });
    }, 2000);
  }, []);

  // Optimistic handler: cuando se asienta una recepción en rampa
  const handleReceptionSuccess = useCallback((receivedLines?: OABLineItem[]) => {
    if (receivedLines && receivedLines.length > 0) {
      setItems(prevItems => {
        return prevItems.map(item => {
          const line = receivedLines.find(
            l => (l.dashboardId && l.dashboardId === item.id) ||
                 (l.insumoId && l.insumoId === item.insumoId) ||
                 (l.nombre && item.nombre && l.nombre.toLowerCase().trim() === item.nombre.toLowerCase().trim())
          );
          if (!line) return item;
          const receivedQty = Number(line.cantidadRecibida) || 0;
          if (receivedQty <= 0) return item;
          const newStock = (item.stockBase || 0) + receivedQty;
          const newTransit = Math.max(0, (item.enTransitoOAB || 0) - receivedQty);
          const newProyectado = newStock + newTransit;
          const newDeficit = Math.max(0, (item.stockMinimo || 0) - newStock);
          return {
            ...item,
            stockBase: newStock,
            enTransitoOAB: newTransit,
            stockProyectado: newProyectado,
            deficit: newDeficit,
            isOptimisticSync: true,
            syncNote: `+${receivedQty} ingresado a existencias`
          };
        });
      });
    }

    // Reconciliar con Notion en segundo plano tras 2 segundos
    setTimeout(() => {
      setItems(current => {
        revalidateInventoryLive(current).then(res => {
          if (res.synced) setItems(res.updatedItems);
        });
        return current;
      });
    }, 2000);
  }, []);

  // Handler para Re-cotización asistida por quiebre de stock en proveedor (PLAN-040 / Decisión Q1)
  const handleRequestRequoteFromRamp = useCallback((requoteItems: OABLineItem[], originalFolio: string) => {
    setReceptionModalOpen(false);
    setSupplyInitialLines(requoteItems);
    setSupplyInitialNotes(`Re-cotización de saldo faltante (${requoteItems.length} renglones) por quiebre de stock en proveedor original de la orden ${originalFolio}.`);
    setSupplyModalOpen(true);
    showToast(`🔄 Cargadas ${requoteItems.length} líneas en borrador para cotizar con otro proveedor.`);
  }, []);

  // Handler para Conteo Cíclico y Ajustes de Kardex (Fase 9F - Decisión /grill-me)
  const handleOpenAdjustmentModal = useCallback((preselected: InventoryItem | null = null) => {
    if (!auth.hasPermission('Auditoria_Kardex') && !auth.hasPermission('Superadmin')) {
      setDeniedTargetModule('Conteo Cíclico y Ajustes de Kardex');
      setDeniedModalOpen(true);
      auth.triggerHaptic('error');
      return;
    }
    setAdjustmentPreselectedItem(preselected);
    setAdjustmentModalOpen(true);
  }, [auth]);

  // Handler para Atajo Contextual de Rampa desde Tránsito (+328) (Fase 9O)
  const handleOpenReceptionForItem = useCallback((item: InventoryItem) => {
    if (auth.hasPermission('Recepcion_Rampa') || auth.hasPermission('Superadmin')) {
      setReceptionInitialSearch(item.nombre || item.codigo || '');
      setReceptionModalOpen(true);
    } else {
      setDeniedTargetModule('Recepción en Rampa');
      setDeniedModalOpen(true);
      auth.triggerHaptic('error');
    }
  }, [auth]);

  const handleAdjustmentSuccess = useCallback((result: StockAdjustmentResult) => {
    showToast(result.message);
    auth.triggerHaptic('success');

    if (result.newStock !== undefined) {
      const targetDashboardId = result.dashboardId;
      const targetInsumoId = result.insumoId;
      const targetName = result.itemNombre;
      const preselectedId = adjustmentPreselectedItem?.id;

      let matchedDashboardId: string | null = null;

      setItems(prevItems => {
        const nextItems = prevItems.map(item => {
          const isTarget = (targetDashboardId && item.id === targetDashboardId) ||
                           (preselectedId && item.id === preselectedId) ||
                           (targetInsumoId && item.insumoId === targetInsumoId) ||
                           (targetName && item.nombre.toLowerCase().trim() === targetName.toLowerCase().trim());
          if (isTarget) {
            matchedDashboardId = item.id;
            const newStock = result.newStock!;
            const newDeficit = Math.max(0, item.stockMinimo - newStock);
            let newEstado = '🟢 En Stock';
            if (newStock === 0) newEstado = '🔴 Sin Stock';
            else if (newStock < item.stockMinimo) newEstado = '🟠 Bajo Mínimo';

            return {
              ...item,
              stockBase: newStock,
              costoUnitarioUSD: result.newCost !== undefined ? result.newCost : item.costoUnitarioUSD,
              deficit: newDeficit,
              estadoStock: result.nuevoEstadoStock || newEstado,
              stockProyectado: newStock + (item.enTransitoOAB || 0),
              seReconto3D: true,
              seRecontoHoy: true,
              diasDesdeReconteo: 0,
              ultimaFechaReconteo: new Date().toISOString().split('T')[0],
              necesitaReconteo: false
            };
          }
          return item;
        });

        // Recalcular baseKpis en caliente (Fase 9H - PLAN-049)
        const total = nextItems.length;
        const sinStock = nextItems.filter(i => (i.stockBase || 0) === 0 && (i.stockMinimo || 0) > 0 && i.prioridad !== 'Por Pedido' && i.estadoStock !== 'Por Pedido').length;
        const bajoMinimo = nextItems.filter(i => (i.stockBase || 0) > 0 && (i.stockBase || 0) < i.stockMinimo && i.prioridad !== 'Por Pedido').length;
        const enStock = nextItems.filter(i => (i.stockBase || 0) >= i.stockMinimo && (i.stockBase || 0) > 0).length;
        const porPedido = nextItems.filter(i => i.prioridad === 'Por Pedido' || i.estadoStock === 'Por Pedido').length;
        const reconteo3D = nextItems.filter(i => i.seReconto3D).length;
        const reconteo3DPct = total > 0 ? Math.round((reconteo3D / total) * 100) : 0;

        setBaseKpis(prev => ({
          ...prev,
          total,
          estado: {
            ...prev.estado,
            sinStock,
            bajoMinimo,
            enStock,
          },
          prioridad: {
            ...prev.prioridad,
            porPedido
          },
          auditados3D: reconteo3D,
          auditados3DPct: reconteo3DPct,
        }));

        return nextItems;
      });

      // Persistir override en localStorage para sobrevivir recargas F5 (Decisión D3)
      const finalIdForOverride = targetDashboardId || matchedDashboardId || preselectedId;
      if (finalIdForOverride) {
        saveStockOverride(finalIdForOverride, result.newStock, result.nuevoEstadoStock);
      }
    }

    // Si el conteo cíclico truncó sobre-reservas MTO o el ítem tenía reservas, refrescar Edge KV en caliente (PLAN-057)
    if (result.mtoReconciliation || allocationsData) {
      loadAllocations();
    }

    // Atajo asistido para reposición urgente OAB por colisión MTO en conteo cíclico (PLAN-057)
    if (result.requestOabReplenishmentQty && result.requestOabReplenishmentQty > 0) {
      const targetItem = items.find(
        i =>
          (result.dashboardId && i.id === result.dashboardId) ||
          (adjustmentPreselectedItem?.id && i.id === adjustmentPreselectedItem.id) ||
          (result.insumoId && i.insumoId === result.insumoId)
      ) || adjustmentPreselectedItem;

      if (targetItem) {
        const reqQty = result.requestOabReplenishmentQty;
        const unitCost = result.newCost || targetItem.costoUnitarioUSD || 1.0;
        const uom = targetItem.unidad || targetItem.unidadMedida || 'UND';
        const oabLine: OABLineItem = {
          nombre: targetItem.nombre,
          insumoId: targetItem.insumoId || targetItem.id,
          dashboardId: targetItem.id,
          cantidadStock: result.newStock ?? targetItem.stockBase,
          stockMinimo: targetItem.stockMinimo || 0,
          deficit: Math.max(reqQty, (targetItem.stockMinimo || 0) - (result.newStock ?? targetItem.stockBase)),
          cantidadSugerida: reqQty,
          cantidadSolicitada: reqQty,
          cantidadAprobada: reqQty,
          costoUnitarioUSD: unitCost,
          subtotalUSD: Math.round(reqQty * unitCost * 100) / 100,
          prioridad: 'Urgente',
          unidadMedida: uom,
          uomCompra: uom,
          factorUoM: 1.0,
          cantidadCompra: reqQty,
          costoCompraUSD: unitCost,
          cantEnBulto: targetItem.cant_en_bulto
        };
        setDraftItemsForModal([]);
        setSupplyInitialLines([oabLine]);
        setSupplyInitialNotes(
          `Reposición urgente por colisión de reserva MTO detectada en conteo cíclico${result.folio ? ` (${result.folio})` : ''}: faltante de ${reqQty} ${uom}.`
        );
        setSupplyModalOpen(true);
      }
    }

    setAdjustmentPreselectedItem(null);
    refreshOfflineCount();

    // Reconciliar con Notion en segundo plano tras 2.5 segundos (SWR anti-flicker Fase 9G)
    setTimeout(() => {
      setItems(current => {
        revalidateInventoryLive(current).then(res => {
          if (res.synced) setItems(res.updatedItems);
        }).catch(err => console.warn('Background SWR post-ajuste:', err));
        return current;
      });
    }, 2500);
  }, [adjustmentPreselectedItem, allocationsData, auth, items, loadAllocations, showToast, refreshOfflineCount]);

  // Handler para Reversión de Ajustes de Kardex (Fase 9I - Contra-asiento Odoo 18)
  const handleReversalSuccess = useCallback((result: ReverseKardexResult) => {
    showToast(result.message);
    auth.triggerHaptic('success');

    if (result.newStock !== undefined) {
      const targetDashboardId = result.dashboardId;
      const targetInsumoId = result.insumoId;
      const targetName = result.itemNombre;

      let matchedDashboardId: string | null = null;

      setItems(prevItems => {
        const nextItems = prevItems.map(item => {
          const isTarget = (targetDashboardId && item.id === targetDashboardId) ||
                           (targetInsumoId && item.insumoId === targetInsumoId) ||
                           (targetName && item.nombre.toLowerCase().trim() === targetName.toLowerCase().trim());
          if (isTarget) {
            matchedDashboardId = item.id;
            const newStock = result.newStock;
            const newDeficit = Math.max(0, item.stockMinimo - newStock);
            let newEstado = '🟢 En Stock';
            if (newStock === 0) newEstado = '🔴 Sin Stock';
            else if (newStock < item.stockMinimo) newEstado = '🟠 Bajo Mínimo';

            return {
              ...item,
              stockBase: newStock,
              deficit: newDeficit,
              estadoStock: result.nuevoEstadoStock 
                ? (result.nuevoEstadoStock === 'En Stock' ? '🟢 En Stock' : result.nuevoEstadoStock === 'Bajo Mínimo' ? '🟠 Bajo Mínimo' : '🔴 Sin Stock') 
                : newEstado,
              stockProyectado: newStock + (item.enTransitoOAB || 0),
              isOptimisticSync: true,
              syncNote: `Reversión formal ${result.folioReverso} aplicada`
            };
          }
          return item;
        });

        // Recalcular baseKpis en caliente (PLAN-049)
        const total = nextItems.length;
        const sinStock = nextItems.filter(i => (i.stockBase || 0) === 0 && (i.stockMinimo || 0) > 0 && i.prioridad !== 'Por Pedido' && i.estadoStock !== 'Por Pedido').length;
        const bajoMinimo = nextItems.filter(i => (i.stockBase || 0) > 0 && (i.stockBase || 0) < i.stockMinimo && i.prioridad !== 'Por Pedido').length;
        const enStock = nextItems.filter(i => (i.stockBase || 0) >= i.stockMinimo && (i.stockBase || 0) > 0).length;
        const porPedido = nextItems.filter(i => i.prioridad === 'Por Pedido' || i.estadoStock === 'Por Pedido').length;

        setBaseKpis(prev => ({
          ...prev,
          total,
          estado: {
            ...prev.estado,
            sinStock,
            bajoMinimo,
            enStock,
          },
          prioridad: {
            ...prev.prioridad,
            porPedido
          }
        }));

        return nextItems;
      });

      // Persistir override en localStorage para resistir recargas F5
      const finalIdForOverride = targetDashboardId || matchedDashboardId;
      if (finalIdForOverride) {
        saveStockOverride(finalIdForOverride, result.newStock, result.nuevoEstadoStock);
      }
    }

    // Reconciliar con Notion en segundo plano tras 2.5 segundos
    setTimeout(() => {
      setItems(current => {
        revalidateInventoryLive(current).then(res => {
          if (res.synced) setItems(res.updatedItems);
        }).catch(err => console.warn('Background SWR post-reversión:', err));
        return current;
      });
    }, 2500);
  }, [auth, showToast]);

  // View Mode changes
  const handleSetViewMode = (mode: 'compact' | 'expanded') => {
    setViewMode(mode);
    if (mode === 'compact') {
      setVisibleColumns(compactColumnKeys);
    } else {
      setVisibleColumns(allColumnsDef.map(c => c.key));
    }
  };

  const handleToggleColumn = (key: string) => {
    setVisibleColumns(prev => {
      const idx = prev.indexOf(key);
      if (idx >= 0) {
        return prev.filter(k => k !== key);
      } else {
        const order = allColumnsDef.map(c => c.key);
        const next = [...prev, key];
        next.sort((a, b) => order.indexOf(a) - order.indexOf(b));
        return next;
      }
    });
    setViewMode('custom');
  };

  // Sorting
  const handleToggleSort = (key: string) => {
    setSortLevels(prev => {
      if (prev.length > 0 && prev[0].key === key) {
        if (prev[0].dir === 'asc') return [{ key, dir: 'desc' as const }, ...prev.slice(1)];
        return prev.slice(1);
      }
      return [{ key, dir: 'asc' as const }, ...prev.filter(l => l.key !== key)].slice(0, 3);
    });
  };

  const handleAddSortLevel = (key: string, dir: 'asc' | 'desc') => {
    setSortLevels(prev => [...prev.filter(l => l.key !== key), { key, dir }].slice(0, 3));
  };

  const handleRemoveSortLevel = (idx: number) => {
    setSortLevels(prev => prev.filter((_, i) => i !== idx));
  };

  // Quick Filters
  const handleToggleQuickFilter = (group: 'estadoStock' | 'prioridad', value: string) => {
    setQuickFilters(prev => {
      const arr = prev[group];
      const nextArr = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
      return { ...prev, [group]: nextArr };
    });
  };

  const handleFilterByStatusFromKpi = (status: string) => {
    handleToggleQuickFilter('estadoStock', status);
  };

  // Reset
  const handleResetAll = () => {
    setSearchQuery('');
    setSortLevels([
      { key: 'prioridad', dir: 'asc' },
      { key: 'deficit', dir: 'desc' }
    ]);
    setQuickFilters({ estadoStock: [], prioridad: [] });
    setGroupByKey(null);
    setOnlyDeficit(false);
    setOnlyPendingRecount(false);
    handleSetViewMode('compact');
  };

  // Active filters check
  const hasActiveFilters = useMemo(() => {
    return (
      searchQuery.trim() !== '' ||
      quickFilters.estadoStock.length > 0 ||
      quickFilters.prioridad.length > 0 ||
      onlyDeficit ||
      onlyPendingRecount ||
      groupByKey !== null ||
      viewMode !== 'compact'
    );
  }, [searchQuery, quickFilters, onlyDeficit, onlyPendingRecount, groupByKey, viewMode]);

  // Contador de artículos pendientes de reconteo físico (>3 días sin auditar o nunca contados)
  const pendingRecountCount = useMemo(() => {
    return items.filter(i => !i.seReconto3D || i.diasDesdeReconteo == null || i.diasDesdeReconteo > 3).length;
  }, [items]);

  // Derivación reactiva anti-SWR race condition: enriquece siempre stockApartado y stockLibre desde allocationsData (PLAN-057)
  const enrichedItems = useMemo(() => {
    if (!allocationsData) return items;
    return items.map(it => {
      const sum = getInsumoAllocationSummary(allocationsData, it);
      const ap = sum?.totalApartado || 0;
      return {
        ...it,
        stockApartado: ap,
        stockLibre: Math.max(0, (it.stockBase || 0) - ap)
      };
    });
  }, [items, allocationsData]);

  // Filtering & Sorting Process (Odoo 18 domain: [('is_storable', '=', True)])
  const filteredAndSortedItems = useMemo(() => {
    let result = enrichedItems.filter(it => it.isStorable !== false && it.contando !== false);

    // 1. Text search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(i =>
        (i.nombre && i.nombre.toLowerCase().includes(q)) ||
        (i.nombreISO && i.nombreISO.toLowerCase().includes(q)) ||
        (i.codigo && i.codigo.toLowerCase().includes(q)) ||
        (i.marca && i.marca.toLowerCase().includes(q)) ||
        (i.categoriaMaterial && i.categoriaMaterial.toLowerCase().includes(q))
      );
    }

    // 2. Quick filters
    if (quickFilters.estadoStock.length > 0) {
      result = result.filter(i => quickFilters.estadoStock.includes(i.estadoStock));
    }
    if (quickFilters.prioridad.length > 0) {
      result = result.filter(i => quickFilters.prioridad.includes(i.prioridad));
    }

    // 3. Only deficit
    if (onlyDeficit) {
      result = result.filter(i => (i.deficit || 0) > 0);
    }

    // 3.5 Only pending recount (>3D)
    if (onlyPendingRecount) {
      result = result.filter(i => !i.seReconto3D || i.diasDesdeReconteo == null || i.diasDesdeReconteo > 3);
    }

    // 4. Sorting
    if (sortLevels.length > 0) {
      result.sort((a, b) => {
        for (const lvl of sortLevels) {
          const valA = (a as any)[lvl.key];
          const valB = (b as any)[lvl.key];

          const order = selectOrders[lvl.key];
          if (order) {
            const ai = order.indexOf(valA);
            const bi = order.indexOf(valB);
            const cmp = (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
            if (cmp !== 0) return lvl.dir === 'asc' ? cmp : -cmp;
          } else {
            if (valA == null && valB == null) continue;
            if (valA == null) return 1;
            if (valB == null) return -1;

            if (typeof valA === 'number' && typeof valB === 'number') {
              const diff = valA - valB;
              if (diff !== 0) return lvl.dir === 'asc' ? diff : -diff;
            } else if (typeof valA === 'boolean' && typeof valB === 'boolean') {
              const diff = valA === valB ? 0 : valA ? -1 : 1;
              if (diff !== 0) return lvl.dir === 'asc' ? diff : -diff;
            } else {
              const cmp = String(valA).localeCompare(String(valB), 'es');
              if (cmp !== 0) return lvl.dir === 'asc' ? cmp : -cmp;
            }
          }
        }
        return 0;
      });
    }

    return result;
  }, [enrichedItems, searchQuery, quickFilters, onlyDeficit, onlyPendingRecount, sortLevels, selectOrders]);

  // Dynamic KPIs from filtered items
  const activeKpis = useMemo(() => {
    if (!hasActiveFilters) return baseKpis;

    const fk: KpiSummary = {
      total: filteredAndSortedItems.length,
      estado: { sinStock: 0, bajoMinimo: 0, enStock: 0, enReconteo: 0, descontinuado: 0 },
      prioridad: { urgente: 0, alta: 0, media: 0, baja: 0, porPedido: 0 },
      auditados3D: 0,
      auditados3DPct: 0,
    };

    for (const item of filteredAndSortedItems) {
      if (item.estadoStock === 'Sin Stock') fk.estado.sinStock++;
      else if (item.estadoStock === 'Bajo Mínimo') fk.estado.bajoMinimo++;
      else if (item.estadoStock === 'En Stock') fk.estado.enStock++;
      else if (item.estadoStock === 'En Reconteo') fk.estado.enReconteo++;
      else if (item.estadoStock === 'Descontinuado') fk.estado.descontinuado++;

      if (item.prioridad === 'Urgente') fk.prioridad.urgente++;
      else if (item.prioridad === 'Alta') fk.prioridad.alta++;
      else if (item.prioridad === 'Media') fk.prioridad.media++;
      else if (item.prioridad === 'Baja') fk.prioridad.baja++;
      else if (item.prioridad === 'Por Pedido' || item.estadoStock === 'Por Pedido') fk.prioridad.porPedido++;

      if (item.seReconto3D) fk.auditados3D++;
    }

    fk.auditados3DPct =
      filteredAndSortedItems.length > 0
        ? Math.round((fk.auditados3D / filteredAndSortedItems.length) * 100)
        : 0;

    return fk;
  }, [filteredAndSortedItems, hasActiveFilters, baseKpis]);

  // Agregar insumo a la Bandeja Flotante de Requisición (acordado en Grill-Me)
  const handleAddToDraft = (item: InventoryItem) => {
    if (!auth.hasPermission('Emitir_OAB')) {
      setDeniedTargetModule('Emisión de Órdenes de Abastecimiento (OAB)');
      setDeniedModalOpen(true);
      auth.triggerHaptic('error');
      return;
    }

    setDraftBasket(prev => {
      const alreadyExists = prev.some(i => i.id === item.id);
      if (alreadyExists) {
        showToast(`"${item.nombre}" ya está seleccionado en la bandeja.`);
        auth.triggerHaptic('warning');
        return prev;
      }
      showToast(`✓ "${item.nombre}" agregado a la bandeja (${prev.length + 1})`);
      auth.triggerHaptic('success');
      return [...prev, item];
    });
  };

  // Despacho directo a taller desde reservas MTO (Popover de Apartado o Tablero Panorámico)
  const handleOpenDispatchFromAlloc = (alloc: StoreAllocation, item?: InventoryItem) => {
    if (!auth.hasPermission('Despacho_Taller') && !auth.hasPermission('Superadmin')) {
      setDeniedTargetModule('Terminal de Despacho a Taller');
      setDeniedModalOpen(true);
      auth.triggerHaptic('error');
      return;
    }

    const targetItem =
      item ||
      enrichedItems.find(
        i =>
          i.id === alloc.dashboardId ||
          (alloc.insumoId && i.id === alloc.insumoId) ||
          (i.insumoId && (i.insumoId === alloc.insumoId || i.insumoId === alloc.dashboardId)) ||
          (i.codigo && alloc.codigo && i.codigo.toLowerCase() === alloc.codigo.toLowerCase())
      ) ||
      null;

    // Cierre limpio de modales previos para evitar apilamiento
    setStoreAllocationModalOpen(false);

    setDispatchPreselectedItem(targetItem);
    setDispatchPreselectedOrder({
      id: alloc.pedidoId || alloc.proyectoId,
      codigo: alloc.pedidoCodigo || '',
      cliente: '',
      proyecto: alloc.proyectoNombre,
      proyectoId: alloc.proyectoId,
      tipo: 'PED'
    });
    setDispatchPreselectedQty(alloc.cantidadApartada > 0 ? alloc.cantidadApartada : 1);
    setDispatchPreselectedNivel('TIENDA');
    setDispatchModalOpen(true);
  };

  return (
    <div className="bg-page text-slate-100 min-h-screen flex flex-col antialiased no-print">
      {/* Header */}
      <Header
        lastSyncDisplay={lastSyncDisplay}
        bcvRate={bcvRate}
        bcvStatus={bcvStatus}
        bcvUpdatedAt={bcvUpdatedAt}
        onOpenSupplyModal={() => {
          if (auth.hasPermission('Emitir_OAB')) {
            setDraftItemsForModal(draftBasket);
            setSupplyModalOpen(true);
          } else {
            setDeniedTargetModule('Emisión de Órdenes de Abastecimiento (OAB)');
            setDeniedModalOpen(true);
            auth.triggerHaptic('error');
          }
        }}
        onOpenReviewModal={() => {
          if (auth.canCotizarOAB || auth.canAprobarOAB || auth.canDelegarOAB || auth.hasPermission('Revisar_OAB')) {
            setReviewModalOpen(true);
          } else {
            setDeniedTargetModule('Revisión y Transcripción de Compras');
            setDeniedModalOpen(true);
            auth.triggerHaptic('error');
          }
        }}
        onOpenReceptionModal={() => {
          if (auth.hasPermission('Recepcion_Rampa')) {
            setReceptionModalOpen(true);
          } else {
            setDeniedTargetModule('Terminal de Recepción en Rampa');
            setDeniedModalOpen(true);
            auth.triggerHaptic('error');
          }
        }}
        onOpenDispatchModal={() => {
          if (auth.hasPermission('Despacho_Taller') || auth.hasPermission('Superadmin')) {
            setDispatchPreselectedItem(null);
            setDispatchModalOpen(true);
          } else {
            setDeniedTargetModule('Terminal de Despacho a Taller');
            setDeniedModalOpen(true);
            auth.triggerHaptic('error');
          }
        }}
        onOpenStoreAllocations={() => {
          setStoreAllocInitialSearch(null);
          setStoreAllocationModalOpen(true);
        }}
        activeAllocationsCount={allocationsData ? Object.keys(allocationsData.summaryByProyectoId || {}).length : 0}
        onOpenCommercialHubModal={() => {
          if (
            !auth.profile ||
            auth.hasPermission('Asignacion_Tiendas') ||
            auth.hasPermission('Emitir_OAB') ||
            auth.hasPermission('Auditoria_BOM') ||
            auth.hasPermission('Auditoria_Kardex') ||
            auth.hasPermission('Superadmin')
          ) {
            setCommercialHubModalOpen(true);
          } else {
            setDeniedTargetModule('Hub Comercial de Proyectos, Clientes y Sucursales (Odoo 18)');
            setDeniedModalOpen(true);
            auth.triggerHaptic('error');
          }
        }}
        onOpenBOMAuditModal={() => {
          if (auth.hasPermission('Auditoria_Kardex') || auth.hasPermission('Superadmin') || auth.hasPermission('Emitir_OAB')) {
            setBomAuditPreselectedOrder(null);
            setBomAuditModalOpen(true);
          } else {
            setDeniedTargetModule('Auditoría BOM y Balance de Tienda');
            setDeniedModalOpen(true);
            auth.triggerHaptic('error');
          }
        }}
        onOpenAdjustmentModal={() => handleOpenAdjustmentModal(null)}
        onOpenBarcodeCollectorModal={() => {
          if (auth.hasPermission('Ajustes' as PermissionKey) || auth.profile?.permissions?.includes('Superadmin')) {
            setBarcodeCollectorOpen(true);
          } else {
            setDeniedTargetModule('Modo Pistola de Código de Barras');
            setDeniedModalOpen(true);
            auth.triggerHaptic('error');
          }
        }}
        scannerQueueCount={scannerQueueCount}
        onOpenKardexModal={() => {
          if (auth.hasPermission('Auditoria_Kardex')) {
            setKardexTargetMaterial(null);
            setKardexModalOpen(true);
          } else {
            setDeniedTargetModule('Libro Mayor de Almacén (Kardex)');
            setDeniedModalOpen(true);
            auth.triggerHaptic('error');
          }
        }}
        onOpenCatalogMaster={() => {
          setCatalogModalInitialTab('directory');
          setCatalogModalInitialItem(null);
          setCatalogModalOpen(true);
        }}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        onRevalidateNotion={handleRevalidateNotion}
        isRevalidatingNotion={isRevalidatingNotion}
        profile={auth.profile}
        onOpenAuditModal={() => setAuditModalOpen(true)}
        onRefreshPermissions={auth.refreshPermissions}
        onChangePin={() => setChangePinModalOpen(true)}
        onLogout={auth.logout}
        isTelegram={auth.isTelegramWebApp}
        offlineQueueCount={offlineQueueCount}
        onOpenOfflineQueueModal={() => setOfflineQueueModalOpen(true)}
      />

      {/* Main Container (1600px North Star) */}
      <main className="flex-1 max-w-[1600px] mx-auto w-full px-4 sm:px-6 py-4 space-y-4">
        {/* KPI Cards */}
        <KpiCards
          kpis={activeKpis}
          isFiltered={hasActiveFilters}
          onFilterByStatus={handleFilterByStatusFromKpi}
          selectedStatus={quickFilters.estadoStock}
        />

        {/* Toolbar & FilterBar */}
        <FilterBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          viewMode={viewMode}
          onSetViewMode={handleSetViewMode}
          allColumns={allColumnsDef}
          visibleColumns={visibleColumns}
          onToggleColumn={handleToggleColumn}
          sortLevels={sortLevels}
          onAddSortLevel={handleAddSortLevel}
          onRemoveSortLevel={handleRemoveSortLevel}
          groupByKey={groupByKey}
          onSetGroupBy={setGroupByKey}
          quickFilters={quickFilters}
          onToggleQuickFilter={handleToggleQuickFilter}
          onResetAll={handleResetAll}
          hasActiveFilters={hasActiveFilters}
          filteredCount={filteredAndSortedItems.length}
          totalCount={items.length}
          onlyDeficit={onlyDeficit}
          onToggleOnlyDeficit={() => setOnlyDeficit(!onlyDeficit)}
          onlyPendingRecount={onlyPendingRecount}
          onToggleOnlyPendingRecount={() => setOnlyPendingRecount(!onlyPendingRecount)}
          pendingRecountCount={pendingRecountCount}
          onOpenPrintModal={() => setPrintSheetModalOpen(true)}
        />

        {/* Inventory Data Table */}
        <InventoryTable
          items={filteredAndSortedItems}
          allColumns={allColumnsDef}
          visibleColumns={visibleColumns}
          sortLevels={sortLevels}
          canVerCostos={auth.canVerCostos}
          onToggleSort={handleToggleSort}
          groupByKey={groupByKey}
          selectOrders={selectOrders}
          onAddToDraft={handleAddToDraft}
          onOpenCatalogItem={(row) => {
            const catItem: CatalogItem = {
              id: row.insumoId || row.id,
              insumoId: row.insumoId || row.id,
              dashboardId: row.id,
              nombre: row.nombre,
              nombreISO: row.nombreISO,
              codigo: row.codigo || '',
              conceptoId: row.conceptoId || undefined,
              largo: row.largo != null ? Number(row.largo) || null : undefined,
              ancho: row.ancho != null ? Number(row.ancho) || null : undefined,
              espesor: row.espesor != null ? Number(row.espesor) || null : undefined,
              ubicacion: row.ubicacion || '',
              categoria: row.categoriaMaterial || 'General',
              rolMaterial: row.rolMaterial || 'Materia Prima',
              unidad: row.unidad || 'UND',
              costoUnitarioUSD: row.costoUnitarioUSD || 0,
              color: row.color || '',
              stockBase: row.stockBase || 0,
              stockMinimo: row.stockMinimo || 0,
              estadoStock: row.estadoStock || 'En Stock',
              contando: true,
              isStorable: row.isStorable !== undefined ? row.isStorable : true,
              activo: row.estadoStock !== 'Descontinuado'
            };
            setCatalogModalInitialTab('form');
            setCatalogModalInitialItem(catItem);
            setCatalogModalOpen(true);
          }}
          onOpenKardexItem={(item) => {
            if (auth.hasPermission('Auditoria_Kardex')) {
              setKardexTargetMaterial({ id: item.id, nombre: item.nombre, stock: item.stockBase });
              setKardexModalOpen(true);
            } else {
              setDeniedTargetModule('Libro Mayor de Almacén (Kardex)');
              setDeniedModalOpen(true);
              auth.triggerHaptic('error');
            }
          }}
          onOpenDispatchItem={(item) => {
            if (auth.hasPermission('Despacho_Taller') || auth.hasPermission('Superadmin')) {
              setDispatchPreselectedItem(item);
              setDispatchModalOpen(true);
            } else {
              setDeniedTargetModule('Terminal de Despacho a Taller');
              setDeniedModalOpen(true);
              auth.triggerHaptic('error');
            }
          }}
          onOpenAdjustmentItem={(item) => handleOpenAdjustmentModal(item)}
          onOpenReceptionItem={(item) => handleOpenReceptionForItem(item)}
          allocationsMap={allocationsData?.summaryByDashboardId}
          onOpenStoreAllocations={(item, alloc) => {
            setSelectedItemForAlloc(item || null);
            setSelectedAllocForReassign(alloc || null);
            setStoreAllocationModalOpen(true);
          }}
          onOpenDirectAllocate={(item) => {
            setSelectedItemForAlloc(item);
            setSelectedAllocForReassign(null);
            setStoreAllocationModalOpen(true);
          }}
          onOpenReassignAlloc={(alloc, item) => {
            setSelectedAllocForReassign(alloc);
            setSelectedItemForAlloc(item);
            setStoreAllocationModalOpen(true);
          }}
          onDispatchAlloc={(alloc, item) => handleOpenDispatchFromAlloc(alloc, item)}
          onOpenRampaForAlloc={(alloc, item) => {
            setReceptionInitialSearch(alloc.folioOAB || alloc.pedidoCodigo || item.codigo || item.nombre);
            setReceptionModalOpen(true);
          }}
          onReprintLabel={(alloc, item) => {
            const cant = alloc.cantidadApartada || 1;
            const cantBulto = item.cant_en_bulto || 1;
            const bultos = Math.max(1, Math.ceil(cant / cantBulto));
            setReprintLabelsItems([{
              folioOAB: alloc.folioOAB || 'MTO-DIRECT',
              nombre: alloc.insumoNombre,
              codigo: item.codigo || alloc.insumoId || '',
              categoria: item.categoriaMaterial || 'MTO Directo',
              proyectoNombre: alloc.proyectoNombre,
              cantidadRecibidaHoy: cant,
              bultos: bultos,
              cantEnBulto: cantBulto,
              bultoPico: (cant % cantBulto !== 0) ? (cant % cantBulto) : undefined,
              unidadMedida: item.unidadMedida || item.unidad || 'und',
              fechaRecepcion: new Date().toISOString().split('T')[0]
            }]);
          }}
          loading={loading}
          loadError={loadError}
          onRetry={handleRefresh}
          onResetFilters={handleResetAll}
        />

        {/* Glossary & Reference */}
        <GlossaryModal lastSyncDisplay={lastSyncDisplay} />
      </main>

      {/* Bandeja Flotante de Requisición OAB (Flujo Acumulativo B2B) */}
      {draftBasket.length > 0 && !supplyModalOpen && (
        <div className="fixed bottom-6 right-6 z-40 no-print">
          <div className="bg-slate-900/95 border border-brand-500/50 backdrop-blur-md rounded-2xl shadow-2xl p-2.5 sm:px-4 sm:py-2.5 flex items-center gap-3 ring-1 ring-brand-500/20">
            <div className="flex items-center gap-2">
              <span className="flex h-3 w-3 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-brand-500"></span>
              </span>
              <span className="text-xs font-semibold text-slate-100">
                🛒 <span className="font-mono text-brand-400 font-bold">{draftBasket.length}</span> {draftBasket.length === 1 ? 'material en solicitud' : 'materiales en solicitud'}
              </span>
            </div>

            <div className="h-4 w-px bg-slate-700"></div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => {
                  setDraftItemsForModal(draftBasket);
                  setSupplyModalOpen(true);
                }}
                className="px-3 py-1.5 text-xs font-bold rounded-lg bg-brand-500 hover:bg-brand-400 text-slate-950 transition active:scale-95 shadow-md flex items-center gap-1"
              >
                <span>Crear Requisición (OAB)</span>
                <span className="font-mono text-[10px]">➔</span>
              </button>

              <button
                onClick={() => {
                  setDraftBasket([]);
                  showToast('Bandeja de solicitud vaciada');
                }}
                className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-950/30 rounded-lg transition"
                title="Vaciar bandeja"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifier Flotante */}
      {toastMessage && (
        <div className="fixed top-5 right-5 z-50 no-print">
          <div className="bg-surfaceHigh border border-brand-500/40 text-slate-100 text-xs px-3.5 py-2 rounded-lg shadow-2xl flex items-center gap-2 ring-1 ring-brand-500/20">
            <span className="inline-block w-2 h-2 rounded-full bg-brand-400 animate-pulse"></span>
            <span>{toastMessage}</span>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-borderSubtle py-3 px-4 no-print mt-auto">
        <div className="max-w-[1600px] mx-auto flex flex-wrap items-center justify-between text-xs text-slate-500 gap-2">
          <span>Sanesca Exhibidores C.A. © 2026 · Todos los derechos reservados</span>
          <span className="font-mono">v2.0.0 PRO · Vite + React + Cloudflare Pages Functions</span>
        </div>
      </footer>

      {/* Supply Order Modal (OAB) */}
      {supplyModalOpen && (
        <SupplyOrderModal
          isOpen={supplyModalOpen}
          onClose={() => {
            setSupplyModalOpen(false);
            setSupplyInitialLines(undefined);
            setSupplyInitialNotes(undefined);
          }}
          inventoryItems={items}
          bcvRate={bcvRate}
          initialDraftItems={draftItemsForModal}
          initialDraftLines={supplyInitialLines}
          initialNotes={supplyInitialNotes}
          onOrderCreated={handleOrderCreated}
          onOpenReview={(targetFolio) => {
            setSupplyModalOpen(false);
            setSupplyInitialLines(undefined);
            setSupplyInitialNotes(undefined);
            setDeepLinkFolio(targetFolio || null);
            setReviewModalOpen(true);
          }}
          onItemCreated={(newItem) => {
            setItems(prev => [newItem, ...prev]);
            showToast(`✨ Nuevo insumo ${newItem.nombre} agregado al catálogo y orden.`);
          }}
          currentUser={{
            name: auth.profile?.name,
            permissions: auth.profile?.permissions,
            puestos: auth.profile?.puestos
          }}
        />
      )}

      {/* Review & Transcription Modal (Magaly & Compras) */}
      {reviewModalOpen && (
        <ErrorBoundary fallbackTitle="Error en Puente Papel-Digital OAB" onReset={() => setReviewModalOpen(false)}>
          <OABReviewModal
            isOpen={reviewModalOpen}
            auth={auth}
            onClose={() => {
              setReviewModalOpen(false);
              setDeepLinkFolio(null);
              if (typeof window !== 'undefined' && window.history?.replaceState) {
                window.history.replaceState({}, '', window.location.pathname);
              }
            }}
            bcvRate={bcvRate}
            initialFolio={deepLinkFolio || undefined}
            onReviewSuccess={handleRefresh}
            onReorderRejected={(rejectedLines, sourceFolio) => {
              const convertedLines: OABLineItem[] = rejectedLines.map(l => {
                const matchedItem = items.find(i => i.id === l.dashboardId || i.insumoId === l.insumoId || (i.nombre && i.nombre.toLowerCase() === l.nombre.toLowerCase()));
                const requested = l.cantidadSolicitada || 1;
                const cost = l.costoUnitarioUSD || matchedItem?.costoUnitarioUSD || 1.0;
                const baseUom = matchedItem?.unidad || matchedItem?.unidadMedida || 'UND';
                return {
                  nombre: l.nombre,
                  insumoId: l.insumoId || l.solicitudId,
                  dashboardId: l.dashboardId || l.solicitudId,
                  cantidadStock: matchedItem?.stockBase || 0,
                  stockMinimo: matchedItem?.stockMinimo || 0,
                  deficit: matchedItem?.deficit || 0,
                  cantidadSugerida: requested,
                  cantidadSolicitada: requested,
                  cantidadAprobada: requested,
                  costoUnitarioUSD: cost,
                  subtotalUSD: requested * cost,
                  prioridad: (l.prioridad as any) || 'Alta',
                  pedidoId: l.pedidoId,
                  proyectoId: l.proyectoId,
                  proyectoNombre: l.proyectoNombre,
                  unidadMedida: baseUom,
                  uomCompra: baseUom,
                  factorUoM: 1.0,
                  cantidadCompra: requested,
                  costoCompraUSD: cost,
                  cantEnBulto: matchedItem?.cant_en_bulto
                };
              });

              setReviewModalOpen(false);
              setDraftItemsForModal([]);
              setSupplyInitialLines(convertedLines);
              setSupplyInitialNotes(`Re-solicitud de ${rejectedLines.length} insumos rechazados originalmente en ${sourceFolio}`);
              setSupplyModalOpen(true);
              showToast(`🔄 Pre-cargando ${rejectedLines.length} insumos rechazados de ${sourceFolio} en nueva requisición.`);
            }}
          />
        </ErrorBoundary>
      )}

      {/* Reception Terminal Modal (Rampa) */}
      {receptionModalOpen && (
        <ReceptionTerminalModal
          isOpen={receptionModalOpen}
          onClose={() => {
            setReceptionModalOpen(false);
            setReceptionInitialSearch(undefined);
          }}
          inventoryItems={items}
          initialSearchTerm={receptionInitialSearch}
          onReceptionSuccess={handleReceptionSuccess}
          onRequestRequote={handleRequestRequoteFromRamp}
        />
      )}

      {/* Material Dispatch Modal (Terminal de Despacho a Taller - Fase 9A) */}
      {dispatchModalOpen && (
        <MaterialDispatchModal
          isOpen={dispatchModalOpen}
          onClose={() => {
            setDispatchModalOpen(false);
            setDispatchPreselectedItem(null);
            setDispatchPreselectedOrder(null);
            setDispatchPreselectedQty(null);
            setDispatchPreselectedNivel('GENERAL');
          }}
          inventoryItems={enrichedItems}
          preselectedItem={dispatchPreselectedItem}
          preselectedOrder={dispatchPreselectedOrder}
          preselectedQty={dispatchPreselectedQty}
          preselectedNivelImputacion={dispatchPreselectedNivel}
          token={auth.token}
          allocationsData={allocationsData}
          onRefreshAllocations={loadAllocations}
          onOpenRampa={(query) => {
            setDispatchModalOpen(false);
            setReceptionInitialSearch(query || dispatchPreselectedItem?.codigo || dispatchPreselectedItem?.nombre);
            setReceptionModalOpen(true);
          }}
          onOpenPurchaseRequisition={(item) => {
            setDispatchModalOpen(false);
            setDraftItemsForModal([item]);
            setSupplyModalOpen(true);
          }}
          onDispatchSuccess={(res) => {
            showToast(`📤 Despacho registrado: ${res.cantidad} und de ${res.materialNombre} a ${res.destino}`);
            setItems(prev => prev.map(item => {
              if (item.nombre === res.materialNombre) {
                const updatedStock = res.nuevoStock;
                const updatedDeficit = Math.max(0, item.stockMinimo - updatedStock);
                return {
                  ...item,
                  stockBase: updatedStock,
                  deficit: updatedDeficit,
                  estadoStock: updatedStock === 0 ? 'Sin Stock' : updatedStock < item.stockMinimo ? 'Bajo Mínimo' : 'En Stock'
                };
              }
              return item;
            }));
            handleRefresh();
          }}
        />
      )}

      {/* Kardex Viewer Modal (Libro Mayor Inmutable) */}
      {kardexModalOpen && (
        <KardexViewerModal
          isOpen={kardexModalOpen}
          onClose={() => {
            setKardexModalOpen(false);
            setDeepLinkFolio(null);
            if (typeof window !== 'undefined' && window.history?.replaceState) {
              window.history.replaceState({}, '', window.location.pathname);
            }
          }}
          initialDashboardId={kardexTargetMaterial?.id}
          initialMaterialName={kardexTargetMaterial?.nombre}
          initialSearchTerm={deepLinkFolio || undefined}
          currentStock={kardexTargetMaterial?.stock}
          onReversalSuccess={handleReversalSuccess}
        />
      )}

      {/* Hub de Clientes, Sucursales y Proyectos de Tienda (PLAN-050 a PLAN-053 Odoo 18) */}
      <CommercialProjectsHubModal
        isOpen={commercialHubModalOpen}
        onClose={() => setCommercialHubModalOpen(false)}
        token={auth.token}
        allocationsData={allocationsData}
        onRefreshAllocations={loadAllocations}
        onOpenStoreAllocations={(projectName) => {
          setStoreAllocInitialSearch(projectName || null);
          setStoreAllocationModalOpen(true);
        }}
        onOpenBOMAuditForOrder={(order) => {
          setBomAuditPreselectedOrder({
            id: order.id,
            codigo: order.codigo,
            nombre: order.proyecto
          });
          setBomAuditModalOpen(true);
        }}
        onOpenSupplyWithDraftLines={(draftLines, notes) => {
          setDraftItemsForModal([]);
          setSupplyInitialLines(draftLines);
          setSupplyInitialNotes(notes);
          setSupplyModalOpen(true);
          showToast(`🛒 Pre-cargando ${draftLines.length} insumos con déficit neto MRP en solicitud de compra OAB.`);
        }}
      />

      {/* Order BOM Audit Modal (Auditoría Ex-Post de Mermas - Fase 9B) */}
      {bomAuditModalOpen && (
        <OrderBOMAuditModal
          isOpen={bomAuditModalOpen}
          onClose={() => {
            setBomAuditModalOpen(false);
            setBomAuditPreselectedOrder(null);
          }}
          initialOrderId={bomAuditPreselectedOrder?.id}
          initialOrderCode={bomAuditPreselectedOrder?.codigo}
          initialOrderName={bomAuditPreselectedOrder?.nombre}
          token={auth.token}
        />
      )}

      {/* PIN Login Modal (Navegador PC) */}
      <PinLoginModal
        isOpen={auth.showPinModal}
        onLogin={auth.loginWithPin}
        onDirectLogin={auth.loginWithCustomSession}
      />

      {/* Access Denied Modal (Zero Trust RBAC Guard) */}
      {(auth.authError || deniedModalOpen) && (
        <AccessDeniedModal
          error={auth.authError || {
            code: 'PERMISSION_REQUIRED',
            message: `Tu puesto de trabajo actual no cuenta con autorización para operar el módulo de ${deniedTargetModule}.`,
            employeeName: auth.profile?.name,
            puestos: auth.profile?.puestos,
            telegramId: typeof auth.profile?.id === 'number' ? auth.profile.id : undefined,
            telegramUsername: auth.profile?.username || undefined
          }}
          targetModule={deniedTargetModule}
          onClose={() => setDeniedModalOpen(false)}
        />
      )}

      {/* Modal de Modificación de PIN */}
      {changePinModalOpen && (
        <ChangePinModal
          isOpen={changePinModalOpen}
          onClose={() => setChangePinModalOpen(false)}
          userName={auth.profile?.name || 'Operador'}
          employeeId={auth.profile?.id}
          isSuperadmin={auth.profile?.permissions.includes('Superadmin')}
        />
      )}

      {/* Visor Forense de Auditoría & Accesos (Superadmin) */}
      {auditModalOpen && (
        <AccessAuditModal
          isOpen={auditModalOpen}
          onClose={() => setAuditModalOpen(false)}
          token={auth.token}
        />
      )}

      {/* Stock Adjustment Modal (Conteo Cíclico & Ajustes - Fase 9F) */}
      {adjustmentModalOpen && (
        <StockAdjustmentModal
          isOpen={adjustmentModalOpen}
          onClose={() => {
            setAdjustmentModalOpen(false);
            setAdjustmentPreselectedItem(null);
          }}
          inventoryItems={enrichedItems}
          preselectedItem={adjustmentPreselectedItem}
          allocationsData={allocationsData}
          onAdjustmentSuccess={handleAdjustmentSuccess}
          bcvRate={bcvRate}
          currentUser={auth.profile}
        />
      )}

      {/* Offline Queue Modal (Cola Local de Conteos - Fase 9G.2) */}
      {offlineQueueModalOpen && (
        <OfflineQueueModal
          isOpen={offlineQueueModalOpen}
          onClose={() => {
            setOfflineQueueModalOpen(false);
            refreshOfflineCount();
          }}
          onSyncComplete={(res) => {
            refreshOfflineCount();
            if (res.syncedCount > 0) {
              showToast(`✅ Sincronizados ${res.syncedCount} conteos de almacén.`);
              setTimeout(() => {
                setItems(current => {
                  revalidateInventoryLive(current).then(r => {
                    if (r.synced) setItems(r.updatedItems);
                  });
                  return current;
                });
              }, 2500);
            }
          }}
          bcvRate={bcvRate}
        />
      )}

      {/* Barcode Collector Modal (Modo Pistola Zero-Mouse - Fase 9J) */}
      <BarcodeCollectorModal
        isOpen={barcodeCollectorOpen}
        onClose={() => setBarcodeCollectorOpen(false)}
        items={items}
        tasaBCV={bcvRate}
      />

      {/* Print Counting Sheet Modal (Planilla Tipo Carta - Fase 9G.2) */}
      {printSheetModalOpen && (
        <PrintCountingSheetModal
          isOpen={printSheetModalOpen}
          onClose={() => setPrintSheetModalOpen(false)}
          allItems={items}
          filteredItems={filteredAndSortedItems}
        />
      )}

      {/* Modal Cédula Digital del Bulto (QR Planta - Micro-Parche 10B.1) */}
      {bultoModalOpen && bultoModalData && (
        <BultoVerificationModal
          isOpen={bultoModalOpen}
          onClose={() => {
            setBultoModalOpen(false);
            setBultoModalData(null);
          }}
          bultoData={bultoModalData}
          isAuthenticated={Boolean(auth.profile)}
          userRole={auth.profile?.puestos?.[0]}
          hasPermission={(perm) => auth.hasPermission(perm as any)}
          onDispatchToTaller={(data) => {
            const foundItem = items.find(it => 
              it.nombre.toLowerCase().includes(data.material.toLowerCase()) || 
              (it.codigo || '').toLowerCase() === data.material.toLowerCase()
            );
            if (foundItem) {
              setDispatchPreselectedItem(foundItem);
            }
            setDispatchModalOpen(true);
          }}
          onReprintLabel={(bData, labelInfo) => {
            const foundItem = items.find(it => 
              it.nombre.toLowerCase().includes(bData.material.toLowerCase()) || 
              (it.codigo || '').toLowerCase() === bData.material.toLowerCase()
            );
            const [bIdx, tBultos] = bData.bulto.includes('_')
              ? bData.bulto.split('_').map(n => parseInt(n, 10) || 1)
              : [1, 1];
            
            const stdCantBulto = foundItem?.cant_en_bulto || 1;
            const cantActual = labelInfo?.saldoRemanente ?? labelInfo?.cantidadRecibidaHoy ?? (labelInfo?.cantEnBulto || stdCantBulto);
            const isAbierto = Boolean(labelInfo?.isBultoAbierto);

            setReprintLabelsItems([{
              folioOAB: bData.folio || 'MTO-TALLER',
              nombre: foundItem?.nombre || bData.material,
              codigo: foundItem?.codigo || bData.material,
              categoria: foundItem?.categoriaMaterial || 'Insumos',
              proyectoNombre: bData.tienda,
              cantidadRecibidaHoy: cantActual,
              bultos: isAbierto ? 1 : tBultos,
              cantEnBulto: cantActual,
              desgloseBultos: labelInfo?.desgloseBultos || (isAbierto ? [cantActual] : undefined),
              unidadMedida: foundItem?.unidadMedida || foundItem?.unidad || bData.unidadMedida || 'und',
              isBultoAbierto: isAbierto,
              saldoRemanente: labelInfo?.saldoRemanente,
              customPkgId: labelInfo?.customPkgId || (isAbierto ? undefined : bData.pkgId),
              pkgIdPrefix: labelInfo?.pkgIdPrefix,
              fechaRecepcion: new Date().toISOString().split('T')[0]
            }]);
          }}
          onOpenOAB={(folio) => {
            setDeepLinkFolio(folio);
            setReviewModalOpen(true);
          }}
          triggerHaptic={(type) => auth.triggerHaptic(type)}
        />
      )}

      {/* Modal de Reimpresión de Etiquetas de Proyecto */}
      {reprintLabelsItems && reprintLabelsItems.length > 0 && (
        <PrintSheetProjectLabels
          onClose={() => setReprintLabelsItems(null)}
          items={reprintLabelsItems}
        />
      )}

      {/* Tablero Panorámico de Asignaciones y Stock Comprometido por Tienda (Fase 10C) */}
      <StoreAllocationModal
        isOpen={storeAllocationModalOpen}
        onClose={() => {
          setStoreAllocationModalOpen(false);
          setSelectedItemForAlloc(null);
          setSelectedAllocForReassign(null);
        }}
        allItems={enrichedItems}
        allocationsData={allocationsData}
        onRefreshAllocations={loadAllocations}
        initialSelectedItem={selectedItemForAlloc}
        initialSelectedAllocation={selectedAllocForReassign}
        initialSearchQuery={storeAllocInitialSearch}
        token={auth.token}
        userRole={auth.profile?.puestos?.[0]}
        hasPermission={(perm) => auth.hasPermission(perm as any)}
        triggerHaptic={(type) => auth.triggerHaptic(type)}
        onReprintLabel={(item) => {
          setReprintLabelsItems([item]);
        }}
        onDispatchAlloc={(alloc, item) => handleOpenDispatchFromAlloc(alloc, item)}
        onOpenBultoModal={(bultoData) => {
          setBultoModalData(bultoData);
          setBultoModalOpen(true);
        }}
      />

      {/* Modal Maestro: Catálogo de Insumos, Ficha Técnica y Health Checker (Fase 11) */}
      <ItemMasterModal
        isOpen={catalogModalOpen}
        onClose={() => {
          setCatalogModalOpen(false);
          setCatalogModalInitialItem(null);
        }}
        initialTab={catalogModalInitialTab}
        initialItem={catalogModalInitialItem}
        onItemUpdated={(_item) => {
          handleRefresh();
        }}
        currentUser={{
          name: auth.profile?.name,
          permissions: auth.profile?.permissions,
          puestos: auth.profile?.puestos
        }}
        bcvRate={bcvRate}
      />
    </div>
  );
}
